import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Layout, Typography, Flex, Spin, Result, Alert, theme, Divider } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, MailOutlined } from '@ant-design/icons';
import { useTranslation } from '../i18n/i18n';
import config from '../config/config';

const { Content } = Layout;
const { Title, Paragraph, Text } = Typography;

const VerifyEmailContent: React.FC = () => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [verifySuccess, setVerifySuccess] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const hasAttemptedVerification = useRef(false);

  useEffect(() => {
    if (hasAttemptedVerification.current) {
      return;
    }

    const token = searchParams.get('token');
    if (!token) {
      setVerifyError(t('verification.error.tokenMissing'));
      setIsLoading(false);
      return;
    }

    const verifyToken = async () => {
      hasAttemptedVerification.current = true;

      try {
        await axios.post(`${config.api.baseURL}${config.api.apiPath}/auth/verify-email`, { token });
        setVerifySuccess(t('verification.success.message'));

        setTimeout(() => {
          window.close();
        }, 5000);
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message || t('verification.error.defaultMessage');

        if (errorMessage.includes('Invalid or expired token')) {
          setVerifyError(t('verification.error.tokenInvalid'));
        } else {
          setVerifyError(errorMessage);
        }
      } finally {
        setIsLoading(false);
      }
    };

    verifyToken();
  }, [searchParams, t]);

  return (
    <Layout style={{ minHeight: '100vh', background: token.colorBgBase }}>
      <Content>
        <Flex
          vertical
          align="center"
          justify="center"
          style={{ minHeight: '100vh', padding: token.padding }}
        >
          <Flex
            vertical
            align="center"
            style={{
              maxWidth: 350,
              width: '100%',
              background: token.colorBgContainer,
              padding: token.padding,
              borderRadius: token.borderRadius,
              boxShadow: token.boxShadow,
            }}
          >
            <Flex vertical align="center" gap={token.margin}>
              <Flex
                align="center"
                justify="center"
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  background: token.colorPrimaryBg,
                }}
              >
                <MailOutlined style={{ fontSize: 32, color: token.colorPrimary }} />
              </Flex>
              <Title level={2} style={{ margin: 0, textAlign: 'center' }}>
                {t('verification.title')}
              </Title>
              <Text type="secondary">{t('verification.platform')}</Text>
            </Flex>

            <Divider />

            {isLoading && (
              <Flex vertical align="center" gap={token.margin}>
                <Spin size="large" />
                <Title level={4} style={{ margin: 0, textAlign: 'center' }}>
                  {t('verification.loading.title')}
                </Title>
                <Paragraph
                  type="secondary"
                  style={{ margin: 0, textAlign: 'center', maxWidth: 400 }}
                >
                  {t('verification.loading.subtitle')}
                </Paragraph>
              </Flex>
            )}

            {!isLoading && verifyError && !verifySuccess && (
              <Result
                status="error"
                icon={<CloseCircleOutlined style={{ color: token.colorError }} />}
                title={t('verification.error.title')}
                subTitle={verifyError}
                extra={
                  <Alert
                    message={t('verification.error.instructions')}
                    type="error"
                    showIcon
                    style={{ maxWidth: 400 }}
                  />
                }
                style={{ padding: 0 }}
              />
            )}

            {!isLoading && verifySuccess && !verifyError && (
              <Result
                status="success"
                icon={<CheckCircleOutlined style={{ color: token.colorSuccess }} />}
                title={t('verification.success.title')}
                style={{ padding: 0 }}
                subTitle={
                  <Flex vertical gap={token.marginSM}>
                    <Text>{verifySuccess}</Text>
                    <Alert
                      message={t('verification.success.badge')}
                      description={t('verification.success.instructions')}
                      type="success"
                    />
                  </Flex>
                }
              />
            )}
          </Flex>
        </Flex>
      </Content>
    </Layout>
  );
};

const VerifyEmail: React.FC = () => {
  return <VerifyEmailContent />;
};

export default VerifyEmail;
